/**
 * The Slime Wars app chunk (ADR-037): the router's lazy import (`App.tsx`) and the front page's
 * prefetch (W032) start the same `import()`, so a prefetched chunk is the one "Play" mounts (and
 * after the full navigation to `?game=wars`, an HTTP-cached one). The Preserve is never prefetched.
 */
export const loadWarsApp = () => import('../wars/WarsApp');

/** TUNING: the idle prefetch waits at most this long for an idle moment after first paint. */
export const PREFETCH_IDLE_TIMEOUT_MS = 4000;
/** Without `requestIdleCallback` (Safari): this long after first paint. */
export const PREFETCH_FALLBACK_MS = 1500;

export interface PrefetchWindow {
  readonly navigator?: { readonly connection?: { readonly saveData?: boolean } };
  requestAnimationFrame?(cb: () => void): number;
  requestIdleCallback?(cb: () => void, options?: { timeout: number }): number;
  cancelIdleCallback?(id: number): void;
  setTimeout(cb: () => void, ms: number): number;
  clearTimeout(id: number): void;
}

export interface Prefetcher {
  /** Starts the import once (hover / focus of the card; the idle moment); a failure may retry. */
  prefetch(): void;
  /**
   * After first paint, when the browser is idle: `prefetch()` — unless the visitor asked to save
   * data (`navigator.connection.saveData`; hover still prefetches). Returns the cancel.
   */
  whenIdle(win?: PrefetchWindow): () => void;
}

export function createPrefetcher(load: () => Promise<unknown>): Prefetcher {
  let started: Promise<unknown> | null = null;
  const prefetch = () => {
    started ??= load().catch(() => {
      started = null; // the router's own import reports a real failure
    });
  };
  return {
    prefetch,
    whenIdle(win = typeof window === 'undefined' ? undefined : (window as PrefetchWindow)) {
      if (win === undefined || win.navigator?.connection?.saveData === true) return () => undefined;
      let cancelled = false;
      let idleId: number | null = null;
      let timerId: number | null = null;
      const run = () => {
        if (!cancelled) prefetch();
      };
      const afterPaint = () => {
        if (cancelled) return;
        if (typeof win.requestIdleCallback === 'function') {
          idleId = win.requestIdleCallback(run, { timeout: PREFETCH_IDLE_TIMEOUT_MS });
        } else {
          timerId = win.setTimeout(run, PREFETCH_FALLBACK_MS);
        }
      };
      // The frame after the first paint (rAF runs before it; the timeout after it).
      if (typeof win.requestAnimationFrame === 'function') {
        win.requestAnimationFrame(() => {
          timerId = win.setTimeout(afterPaint, 0);
        });
      } else {
        timerId = win.setTimeout(afterPaint, 0);
      }
      return () => {
        cancelled = true;
        if (idleId !== null) win.cancelIdleCallback?.(idleId);
        if (timerId !== null) win.clearTimeout(timerId);
      };
    },
  };
}

/** The front page's prefetch of the Slime Wars app. */
export const warsPrefetch: Prefetcher = createPrefetcher(loadWarsApp);
